import ko from "knockout";
import ObservableModel from "util/ObservableModel";
import { User } from "@genesys/cloud-frontend/models/User";
import { UserImage } from "@genesys/cloud-frontend/models/UserImage";
import { Division } from "@genesys/cloud-frontend/models/Division";

const userImageResolutions = [
    "x48",
    "x96",
    "x128",
    "x200",
    "x300",
    "x400",
] as const;

type UserImageResolution = (typeof userImageResolutions)[number];
type UserImageResolutionUrlMap = Record<UserImageResolution, string>;

const NO_IMG = "no_img";
const avatarImageDefaults = Object.freeze(
    userImageResolutions.reduce(
        (acc, curr) => ({ [curr]: NO_IMG, ...acc }),
        {} as UserImageResolutionUrlMap
    )
);

type TObservableModel = typeof ObservableModel;

type ObservableProps<Type> = {
    [Property in keyof Type]: Type[Property] extends Array<Type[Property]>
        ? ko.ObservableArray<Type[Property]>
        : ko.Observable<Type[Property]>;
};

type ObservableUser = Required<Pick<User, "id">> &
    ObservableProps<Omit<User, "id">>;

type ComputedBoolean = ko.Computed<boolean>;

type ObservableUserModelProperties = {
    avatarImages: ko.Computed<UserImageResolutionUrlMap>;
    userImageError: ComputedBoolean;
};

export type ObservableUserModel = TObservableModel &
    ObservableUser &
    ObservableUserModelProperties;

export default ObservableModel.extend(
    {
        defaults: {
            images: [] as UserImage[],
            division: {} as Division,
        },
    },
    function (this: ObservableUserModel) {
        const self = this;

        /**
         * A map of images available to for the user. This method will return an object
         * map whose keys are the resolutions for the image and the value is the URI to
         * the image. For example:
         *
         *   {
         *     x48: http://purecloud.com/path/to/image
         *   }
         *
         * Current supported resolution keys are:
         *
         *   - x48
         *   - x96
         *   - x128
         *   - x200
         *   - x300
         *   - x400
         *
         * The number in the key names indicate the height and width of the image that
         * will be sent back from the provided URI. If an image is not available for a
         * resolution, the URI will be an empty string.
         */
        self.avatarImages = ko.computed(() => {
            const images = self.images?.() ?? [];
            return images.reduce((acc, { resolution, imageUri }) => {
                if (resolution && imageUri) {
                    return { ...acc, [resolution]: imageUri };
                }
                return acc;
            }, avatarImageDefaults);
        });

        self.userImageError = ko.computed(() => {
            return (
                !self.avatarImages() || self.avatarImages()?.["x48"] === NO_IMG
            );
        });

        self.unwrap = () => {
            return ObservableModel.unwrap(self, [
                "avatarImages",
                "unwrap",
                "userImageError",
            ]);
        };
    }
) as any as new (userJSON: User) => ObservableUserModel;
